import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Search, TrendingUp, Bitcoin, BarChart3, X, Command, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TradingSymbol } from '@trading/shared';

interface SymbolSearchProps {
  onSearch: (q: string, signal?: AbortSignal) => Promise<TradingSymbol[]>;
  currentSymbol: string;
  onSelect: (symbolId: string) => void;
  isOpen: boolean;
  onClose: () => void;
}

const CATEGORY_CONFIG: Record<string, { label: string; icon: React.ComponentType<{ className?: string }>; color: string; bgColor: string }> = {
  forex: {
    label: 'Forex',
    icon: TrendingUp,
    color: 'text-profit',
    bgColor: 'bg-profit/10',
  },
  crypto: {
    label: 'Crypto',
    icon: Bitcoin,
    color: 'text-warning',
    bgColor: 'bg-warning/10',
  },
  indices: {
    label: 'Indices',
    icon: BarChart3,
    color: 'text-accent-primary',
    bgColor: 'bg-accent-primary/10',
  },
  stock: {
    label: 'Stock / ETF',
    icon: BarChart3,
    color: 'text-accent-secondary',
    bgColor: 'bg-accent-secondary/10',
  },
};

const DEBOUNCE_MS = 300;

export default function SymbolSearch({
  onSearch,
  currentSymbol,
  onSelect,
  isOpen,
  onClose,
}: SymbolSearchProps) {
  const { t } = useTranslation('analysis');
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [symbols, setSymbols] = useState<TradingSymbol[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Debounced search handler with AbortController so a slow in-flight request
  // from a previous query is cancelled before the new one fires, preventing
  // stale results from overwriting fresher ones.
  const triggerSearch = useCallback((q: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    // Abort any in-flight request immediately
    abortRef.current?.abort();

    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;

      setIsLoading(true);
      try {
        const results = await onSearch(q, controller.signal);
        setSymbols(results);
        setSelectedIndex(0);
      } catch {
        // Silently ignore aborted requests or network errors
        setSymbols([]);
      } finally {
        setIsLoading(false);
      }
    }, DEBOUNCE_MS);
  }, [onSearch]);

  // Group symbols by type
  const groupedSymbols = useMemo(() => {
    const groups: Record<string, TradingSymbol[]> = {};
    symbols.forEach((sym) => {
      const key = sym.type;
      if (!groups[key]) groups[key] = [];
      groups[key].push(sym);
    });
    return groups;
  }, [symbols]);

  // Flat list for keyboard navigation
  const flatList = useMemo(() => {
    const result: { symbol: TradingSymbol; category: string }[] = [];
    Object.entries(groupedSymbols).forEach(([category, items]) => {
      items.forEach((symbol) => {
        result.push({ symbol, category });
      });
    });
    return result;
  }, [groupedSymbols]);

  // Reset state and load defaults when opening
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
      // Load defaults immediately (empty query returns curated list)
      triggerSearch('');
    } else {
      // Clean up on close
      if (debounceRef.current) clearTimeout(debounceRef.current);
    }
  }, [isOpen, triggerSearch]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, flatList.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (flatList[selectedIndex]) {
            onSelect(flatList[selectedIndex].symbol.id);
            onClose();
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, flatList, selectedIndex, onSelect, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && flatList.length > 0) {
      const selectedElement = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      selectedElement?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, flatList.length]);

  if (!isOpen) return null;

  let currentIndex = 0;
  const categoryKeys = Object.keys(groupedSymbols);

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="modal-backdrop"
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed top-[15%] left-1/2 -translate-x-1/2 z-50 w-full max-w-lg">
        <div className="bg-bg-elevated border border-border-default rounded-xl shadow-2xl overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-3 p-4 border-b border-border-subtle">
            {isLoading ? (
              <Loader2 className="w-5 h-5 text-accent-primary flex-shrink-0 animate-spin" />
            ) : (
              <Search className="w-5 h-5 text-text-muted flex-shrink-0" />
            )}
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => {
                const value = e.target.value;
                setSearch(value);
                setSelectedIndex(0);
                triggerSearch(value);
              }}
              placeholder={t('symbolSearch.placeholder')}
              className="flex-1 bg-transparent text-text-primary placeholder-text-muted outline-none text-lg"
              autoComplete="off"
            />
            <div className="flex items-center gap-1 text-xs text-text-muted">
              <kbd className="px-1.5 py-0.5 bg-bg-tertiary rounded text-text-secondary">esc</kbd>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-bg-tertiary rounded transition-colors"
            >
              <X className="w-4 h-4 text-text-muted" />
            </button>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-[400px] overflow-y-auto">
            {isLoading && symbols.length === 0 ? (
              <div className="p-8 text-center text-text-muted">
                {t('symbolSearch.loading')}
              </div>
            ) : !isLoading && symbols.length === 0 ? (
              <div className="p-8 text-center text-text-muted">
                {search ? t('symbolSearch.noResults') : t('symbolSearch.placeholder')}
              </div>
            ) : (
              categoryKeys.map((category) => {
                const items = groupedSymbols[category];
                if (!items || items.length === 0) return null;

                const config = CATEGORY_CONFIG[category] ?? CATEGORY_CONFIG['stock'];
                const Icon = config.icon;

                return (
                  <div key={category}>
                    {/* Category header */}
                    <div className="flex items-center gap-2 px-4 py-2 bg-bg-tertiary/50 sticky top-0">
                      <div className={`p-1 rounded ${config.bgColor}`}>
                        <Icon className={`w-3.5 h-3.5 ${config.color}`} />
                      </div>
                      <span className="text-xs font-medium text-text-secondary uppercase tracking-wider">
                        {config.label}
                      </span>
                      <span className="text-xs text-text-muted">({items.length})</span>
                    </div>

                    {/* Items */}
                    {items.map((symbol) => {
                      const itemIndex = currentIndex++;
                      const isSelected = itemIndex === selectedIndex;
                      const isCurrent = symbol.id === currentSymbol;

                      return (
                        <button
                          key={symbol.id}
                          data-index={itemIndex}
                          onClick={() => {
                            onSelect(symbol.id);
                            onClose();
                          }}
                          className={`
                            w-full flex items-center gap-3 px-4 py-3 transition-colors
                            ${isSelected ? 'bg-accent-primary/20' : 'hover:bg-bg-tertiary'}
                            ${isCurrent ? 'border-l-2 border-accent-primary' : ''}
                          `}
                        >
                          <div className="flex-1 text-left">
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${isSelected ? 'text-accent-primary' : 'text-text-primary'}`}>
                                {symbol.name}
                              </span>
                              {isCurrent && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent-primary/20 text-accent-primary">
                                  CURRENT
                                </span>
                              )}
                            </div>
                            <span className="text-sm text-text-muted">{symbol.displayName}</span>
                          </div>
                          {isSelected && (
                            <span className="text-xs text-text-muted">
                              <kbd className="px-1.5 py-0.5 bg-bg-tertiary rounded">↵</kbd>
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer hint */}
          <div className="flex items-center justify-between px-4 py-2 bg-bg-tertiary/30 border-t border-border-subtle text-xs text-text-muted">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-bg-tertiary rounded">↑</kbd>
                <kbd className="px-1 py-0.5 bg-bg-tertiary rounded">↓</kbd>
                navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-bg-tertiary rounded">↵</kbd>
                select
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Command className="w-3 h-3" />
              <span>K to open</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
